import React from "react";
import {
  Typography,
  Divider,
  Box,
  Grid,
  Link,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import BusinessIcon from "@mui/icons-material/Business";
import ContactPhoneIcon from "@mui/icons-material/ContactPhone";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import BuildIcon from "@mui/icons-material/Build";
import PriceChangeIcon from "@mui/icons-material/PriceChange";
import VerifiedIcon from "@mui/icons-material/Verified";
import LanguageIcon from "@mui/icons-material/Language";
import SettingsIcon from "@mui/icons-material/Settings";
import DescriptionIcon from "@mui/icons-material/Description";
import PersonIcon from "@mui/icons-material/Person";
import InfoIcon from "@mui/icons-material/Info";

const Section = ({ title, icon, children }: { title: string; icon: React.ReactNode; children: React.ReactNode }) => (
  <Accordion defaultExpanded>
    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
      <Typography variant="subtitle1" fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {icon} {title}
      </Typography>
    </AccordionSummary>
    <AccordionDetails>
      <Grid container spacing={2}>{children}</Grid>
    </AccordionDetails>
  </Accordion>
);

const Field = ({ label, value }: { label: string; value: any }) => (
  <Grid item xs={12} sm={4}>
    <Typography variant="body2"><strong>{label}:</strong> {value || "-"}</Typography>
  </Grid>
);

const documentLabelMap: Record<string, string> = {
  gstCertificatePath: "GST Certificate",
  panCardPath: "PAN Card",
  bankChequePath: "Bank Cheque",
  addressProofPath: "Address Proof",
  certificationDocsPath: "Certification Documents",
  bisHallmarkCertificatePath: "BIS Hallmark Certificate",
  giaCertificatePath: "GIA Certificate",
};

const VendorDetailView = ({ vendor }: { vendor: any }) => {
  return (
    <Box>
      <Section title="Basic Info" icon={<InfoIcon />}>
        <Field label="Name" value={vendor.vendorName} />
        <Field label="Status" value={vendor.status} />
      </Section>

      <Section title="Business Info" icon={<BusinessIcon />}>
        <Field label="Business Type" value={vendor.businessInfo?.businessType} />
        <Field label="GST Number" value={vendor.businessInfo?.gstNumber} />
        <Field label="PAN Number" value={vendor.businessInfo?.panNumber} />
        <Field label="Address" value={vendor.businessInfo?.address} />
        <Field label="Operating Locations" value={vendor.businessInfo?.operatingLocations} />
        <Field label="Years in Business" value={vendor.businessInfo?.yearsInBusiness} />
      </Section>

      <Section title="Contact Info" icon={<ContactPhoneIcon />}>
        <Field label="Contact Person" value={vendor.contactInfo?.contactPerson} />
        <Field label="Designation" value={vendor.contactInfo?.designation} />
        <Field label="Phone" value={vendor.contactInfo?.phoneNumber} />
        <Field label="WhatsApp" value={vendor.contactInfo?.whatsappNumber} />
        <Field label="Email" value={vendor.contactInfo?.email} />
      </Section>

      <Section title="Bank Details" icon={<AccountBalanceIcon />}>
        <Field label="Account Name" value={vendor.bankDetails?.bankAccountName} />
        <Field label="Account Number" value={vendor.bankDetails?.bankAccountNumber} />
        <Field label="Bank" value={vendor.bankDetails?.bankName} />
        <Field label="Branch" value={vendor.bankDetails?.bankBranch} />
        <Field label="IFSC" value={vendor.bankDetails?.ifscCode} />
      </Section>

      <Section title="Capabilities" icon={<BuildIcon />}>
        <Field label="Categories" value={vendor.capabilities?.jewelryCategories?.join(", ")} />
        <Field label="Metals Used" value={vendor.capabilities?.metalsUsed} />
        <Field label="Gemstones" value={vendor.capabilities?.gemstonesOffered} />
        <Field label="Customization" value={vendor.capabilities?.supportsCustomization ? "Yes" : "No"} />
        <Field label="Lead Time" value={vendor.capabilities?.averageLeadTime} />
      </Section>

      <Section title="Pricing Policies" icon={<PriceChangeIcon />}>
        <Field label="Pricing Structure" value={vendor.pricingPolicies?.pricingStructure} />
        <Field label="Discount Policy" value={vendor.pricingPolicies?.discountPolicy} />
        <Field label="Return Policy" value={vendor.pricingPolicies?.returnPolicy} />
        <Field label="Shipping Policy" value={vendor.pricingPolicies?.shippingPolicy} />
        <Field label="Payment Terms" value={vendor.pricingPolicies?.paymentTerms} />
      </Section>

      <Section title="Certifications" icon={<VerifiedIcon />}>
        <Field label="Diamond Certification" value={vendor.certifications?.hasDiamondCertification ? "Yes" : "No"} />
        <Field label="Hallmark" value={vendor.certifications?.hasHallmarkCertification ? "Yes" : "No"} />
        <Field label="Bodies" value={vendor.certifications?.certificationBodies} />
        <Field label="Branded Items" value={vendor.certifications?.isAuthorizedForBrandedItems ? "Yes" : "No"} />
      </Section>

      <Section title="Digital Presence" icon={<LanguageIcon />}>
        <Field label="Website" value={vendor.digitalPresence?.websiteUrl} />
        <Field label="Facebook" value={vendor.digitalPresence?.facebookUrl} />
        <Field label="Instagram" value={vendor.digitalPresence?.instagramUrl} />
        <Field label="Twitter" value={vendor.digitalPresence?.twitterUrl} />
        <Field label="LinkedIn" value={vendor.digitalPresence?.linkedInUrl} />
        <Field label="Other Platform" value={vendor.digitalPresence?.otherPlatformUrl} />
        <Field label="Catalog" value={vendor.digitalPresence?.catalogUrl} />
        <Field label="Marketplace Presence" value={vendor.digitalPresence?.marketplacePresence} />
      </Section>

      <Section title="Operational Preferences" icon={<SettingsIcon />}>
        <Field label="Communication Mode" value={vendor.operationalPreferences?.preferredCommunicationMode} />
        <Field label="Order Handling" value={vendor.operationalPreferences?.orderHandlingProcess} />
        <Field label="Inventory Update" value={vendor.operationalPreferences?.inventoryUpdateFrequency} />
        <Field label="Packaging" value={vendor.operationalPreferences?.packagingStandards} />
      </Section>

      <Section title="Documents" icon={<DescriptionIcon />}>
        {Object.entries(vendor.documentReferences || {}).map(([key, url]) => (
          typeof url === "string" && url.trim() !== "" ? (
            <Grid item xs={12} sm={4} key={key}>
              <Link href={url} target="_blank" rel="noreferrer" underline="hover">
                {documentLabelMap[key] || key}
              </Link>
            </Grid>
          ) : null
        ))}
      </Section>

      <Section title="Submitted By" icon={<PersonIcon />}>
        <Field label="Email" value={vendor.submittedBy?.email} />
        <Field label="Mobile" value={vendor.submittedBy?.mobile} />
        <Field label="Referral Code" value={vendor.submittedBy?.myReferralCode} />
        <Field label="Wallet" value={`₹${vendor.submittedBy?.wallet}`} />
        <Grid item xs={12}>
          <Typography variant="body2"><strong>Billing Address:</strong> {vendor.submittedBy?.billingAddress?.billingAddress}, {vendor.submittedBy?.billingAddress?.billingCity}, {vendor.submittedBy?.billingAddress?.billingState} - {vendor.submittedBy?.billingAddress?.billingPostalCode}, {vendor.submittedBy?.billingAddress?.billingCountry}</Typography>
        </Grid>
      </Section>
    </Box>
  );
};

export default VendorDetailView;
